import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Luggage, Plane } from "lucide-react";
import { Empty, Field, PageHeader, PrimaryBtn, inputCls, Opt } from "@/components/Common";
import { AirportSelect, useAirports } from "@/components/HeroSearch";
import { BookingDialog } from "@/components/BookingDialog";
import { CLASS_MULT, addMinutes, hhmm, http, inr, today } from "@/lib/api";

const IMG = "https://images.pexels.com/photos/39785533/pexels-photo-39785533.jpeg?auto=compress&cs=tinysrgb&w=1600";

export default function Flights() {
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState({ from: sp.get("from") || "DEL", to: sp.get("to") || "BOM", date: sp.get("date") || today(7), pax: 1, cls: "economy" });
  const [maxStops, setMaxStops] = useState("any");
  const [airline, setAirline] = useState("all");
  const [selected, setSelected] = useState(null);
  const { data: airports = [] } = useAirports();
  const from = sp.get("from") || "DEL";
  const to = sp.get("to") || "BOM";

  const { data = [], isLoading } = useQuery({
    queryKey: ["flights", from, to],
    queryFn: () => http.get(`/flights/search?from_code=${from}&to_code=${to}`).then((r) => r.data),
  });
  const airlines = useMemo(() => [...new Set(data.map((f) => f.airline))], [data]);
  const shown = data.filter((f) => (maxStops === "any" || f.stops === 0) && (airline === "all" || f.airline === airline));
  const fare = (f) => Math.floor(f.price * CLASS_MULT[q.cls]) * q.pax;
  const city = (c) => airports.find((a) => a.code === c)?.city || c;

  const search = (e) => {
    e.preventDefault();
    setSp({ from: q.from, to: q.to, date: q.date });
  };

  return (
    <div data-testid="flights-page">
      <PageHeader eyebrow="Flights" title="Fly anywhere, pay in rupees." sub="Domestic routes across India and international flights from major Indian hubs." image={IMG} />
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <form onSubmit={search} className="-mt-10 relative grid gap-3 rounded-2xl bg-white p-5 shadow-xl sm:grid-cols-3 lg:grid-cols-6 items-end" data-testid="flight-search-form">
          <Field label="From"><AirportSelect value={q.from} onChange={(v) => setQ({ ...q, from: v })} testid="flight-from-select" /></Field>
          <Field label="To"><AirportSelect value={q.to} onChange={(v) => setQ({ ...q, to: v })} testid="flight-to-select" /></Field>
          <Field label="Departure"><input type="date" min={today()} className={inputCls} value={q.date} onChange={(e) => setQ({ ...q, date: e.target.value })} data-testid="flight-date-input" /></Field>
          <Field label="Passengers"><input type="number" min={1} max={9} className={inputCls} value={q.pax} onChange={(e) => setQ({ ...q, pax: Math.max(1, +e.target.value) })} data-testid="flight-pax-input" /></Field>
          <Field label="Class">
            <select className={inputCls} value={q.cls} onChange={(e) => setQ({ ...q, cls: e.target.value })} data-testid="flight-class-select">
              <option value="economy">Economy</option><option value="premium_economy">Premium Economy</option><option value="business">Business</option>
            </select>
          </Field>
          <PrimaryBtn type="submit" data-testid="flight-search-btn">Search flights</PrimaryBtn>
        </form>

        <div className="mt-12 grid gap-8 lg:grid-cols-[240px_1fr]">
          <aside className="space-y-6">
            <Field label="Stops">
              <select className={inputCls} value={maxStops} onChange={(e) => setMaxStops(e.target.value)} data-testid="filter-stops">
                <option value="any">Any</option><option value="0">Non-stop only</option>
              </select>
            </Field>
            <Field label="Airline">
              <select className={inputCls} value={airline} onChange={(e) => setAirline(e.target.value)} data-testid="filter-airline">
                <option value="all">All airlines</option>{airlines.map((a) => <Opt key={a} v={a} l={a} />)}
              </select>
            </Field>
          </aside>
          <div className="space-y-4">
            <p className="text-sm text-slate-500" data-testid="flight-results-count">{shown.length} flights · {city(from)} → {city(to)} · {sp.get("date") || q.date}</p>
            {isLoading && <div className="h-28 animate-pulse rounded-2xl bg-white" />}
            {!isLoading && !shown.length && <Empty testid="flight-empty">No flights on this route yet. Try routes via Delhi, Mumbai or Bengaluru.</Empty>}
            {shown.map((f) => (
              <div key={f.id} className="lift grid items-center gap-5 rounded-2xl border border-[#E5E0D8] bg-white p-5 sm:grid-cols-[1.2fr_2fr_1fr]" data-testid={`flight-card-${f.flight_no}`}>
                <div className="flex items-center gap-3">
                  <span className="grid h-11 w-11 place-items-center rounded-full bg-[#F4F0EA]"><Plane className="h-5 w-5 text-[#0D1B2A]" /></span>
                  <div><p className="font-semibold">{f.airline}</p><p className="text-xs text-slate-500">{f.flight_no}</p></div>
                </div>
                <div className="flex items-center gap-4">
                  <div><p className="text-xl font-bold">{f.depart_time}</p><p className="text-xs text-slate-500">{f.from_code}</p></div>
                  <div className="flex-1 text-center text-xs text-slate-500">
                    <p>{hhmm(f.duration_min)}</p>
                    <div className="my-1 h-px bg-[#E5E0D8] relative"><span className="absolute left-1/2 -top-1 h-2 w-2 -translate-x-1/2 rounded-full bg-[#E07A5F]" /></div>
                    <p>{f.stops ? `${f.stops} stop` : "Non-stop"}</p>
                  </div>
                  <div><p className="text-xl font-bold">{addMinutes(f.depart_time, f.duration_min)}</p><p className="text-xs text-slate-500">{f.to_code}</p></div>
                </div>
                <div className="flex items-center justify-between gap-3 sm:flex-col sm:items-end">
                  <div className="text-right">
                    <p className="text-xl font-bold text-[#0D1B2A]" data-testid={`flight-price-${f.flight_no}`}>{inr(fare(f))}</p>
                    <p className="flex items-center justify-end gap-1 text-xs text-slate-500"><Luggage className="h-3 w-3" />{f.baggage}</p>
                  </div>
                  <PrimaryBtn className="h-10 px-5" onClick={() => setSelected(f)} data-testid={`flight-book-btn-${f.flight_no}`}>Book</PrimaryBtn>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {selected && (
        <BookingDialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}
          title={`${selected.from_city} → ${selected.to_city}`}
          amount={fare(selected)}
          lines={[["Flight", `${selected.airline} ${selected.flight_no}`], ["Date", sp.get("date") || q.date], ["Departs", selected.depart_time], ["Passengers", q.pax], ["Class", q.cls.replace("_", " ")]]}
          booking={{ type: "flight", item_id: selected.id, travel_date: sp.get("date") || q.date, travellers: q.pax, cabin_class: q.cls }} />
      )}
    </div>
  );
}
